import { useEffect, useState } from 'react';
import type { MeResponse } from '@/lib/api';
import { track } from '@/lib/analytics';
import { cloudSet, getWebApp } from '@/lib/telegram';
import { SetupApp } from './SetupApp';
import { SetupCover } from './SetupCover';
import { SetupDone } from './SetupDone';
import { SetupOtherDevice } from './SetupOtherDevice';
import { SetupSub } from './SetupSub';

type Step = 0 | 1 | 2 | 3 | 'other';

interface SetupScreenProps {
  me: MeResponse;
  onCopy: () => void;
  onFinish: () => void;
  // Lets the profile/help screens jump straight into the "other device"
  // sub-flow (the 2×2 platform picker) without going through the cover.
  // Undefined → start at the cover as usual.
  initialStep?: Step;
}

export function SetupScreen({ me, onCopy, onFinish, initialStep }: SetupScreenProps) {
  const [step, setStep] = useState<Step>(initialStep ?? 0);

  // Telegram's native back button mirrors our internal flow state — visible on
  // every step except the cover, popping one level on tap. The bottom tab bar
  // already gives the user "leave the flow" via direct nav.
  //
  // Two effects on purpose: the cover branch only needs to hide(), so we skip
  // creating a handler we'd never register. That keeps offClick paired
  // exactly with its onClick (the SDK matches by reference).
  useEffect(() => {
    const back = getWebApp()?.BackButton;
    if (!back) return;
    if (step === 0) {
      back.hide();
      return;
    }
    const handler = () => {
      setStep((cur) => {
        if (cur === 'other') return 0;
        if (cur === 1) return 0;
        if (cur === 2) return 1;
        if (cur === 3) return 2;
        return cur;
      });
    };
    back.show();
    back.onClick(handler);
    return () => {
      back.offClick(handler);
    };
  }, [step]);

  // Hide the back button when the screen unmounts (tab switch) so it doesn't
  // linger on the home/profile/help screens.
  useEffect(() => {
    return () => {
      getWebApp()?.BackButton?.hide();
    };
  }, []);

  const finish = () => {
    void cloudSet('setupComplete', '1');
    track('miniapp_setup_completed', { entry: initialStep === 'other' ? 'other' : 'cover' });
    onFinish();
  };

  let view: JSX.Element;
  if (step === 'other') {
    view = <SetupOtherDevice subscriptionUrl={me.subscription.url} onCopy={onCopy} />;
  } else if (step === 0) {
    view = <SetupCover onStart={() => setStep(1)} onOtherDevice={() => setStep('other')} />;
  } else if (step === 1) {
    view = <SetupApp onNext={() => setStep(2)} />;
  } else if (step === 2) {
    view = (
      <SetupSub
        subscriptionUrl={me.subscription.url}
        onNext={() => setStep(3)}
        onBack={() => setStep(1)}
      />
    );
  } else {
    view = <SetupDone onFinish={finish} />;
  }

  return (
    <div
      key={String(step)}
      style={{
        height: '100%',
        position: 'relative',
        animation: 'shushFade 280ms cubic-bezier(.2,.8,.2,1)',
      }}
    >
      {view}
    </div>
  );
}
